import { useState } from "react";
import { toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";
import {
  Send, Mail, MapPin, Clock, Twitter, ChevronLeft, ChevronRight, Check,
  User, Gamepad2, Wrench, CalendarClock, ClipboardCheck, FileText, ShieldCheck,
} from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import SiteNav from "@/components/SiteNav";
import SiteFooter from "@/components/SiteFooter";
import { SERVICES } from "@/content/services";
import Seo from "@/components/Seo";

const EASE = [0.16, 1, 0.3, 1];
const inputCls = "w-full bg-[#050505] border border-white/10 px-3 py-2.5 font-mono text-sm text-zinc-100 rounded-none focus:outline-none focus:ring-1 focus:ring-nc-blue focus:border-nc-blue";
const labelCls = "font-mono text-[10px] tracking-[0.25em] text-zinc-500 uppercase block mb-2";
const hintCls = "font-mono text-[10px] text-zinc-600 mt-1.5 leading-relaxed";

const INFO = [
  { icon: Mail, label: "Email", value: "business@specpixel.com", href: "mailto:business@specpixel.com" },
  { icon: MapPin, label: "Studio", value: "Melbourne, Australia" },
  { icon: Clock, label: "Support", value: "24/7 — quotes back within 24 hours" },
  { icon: Twitter, label: "X / Twitter", value: "@specpixel", href: "https://twitter.com/specpixel" },
];

const ROLES = ["Studio Director / Founder", "Producer / Project Manager", "Lead Developer", "Indie Developer", "Publisher / Investor", "Other"];
const TEAM_SIZES = ["Just me", "2–5", "6–15", "16–50", "50+"];
const PROJECT_TYPES = ["New game — full production", "Existing game — ongoing support", "Cinematics / trailer only", "Asset production only", "Porting / optimization", "Something else"];
const ENGINES = ["Unreal Engine 5", "Unity", "CryEngine", "Custom / Other", "Not decided yet"];
const STAGES = ["Concept / pre-production", "Prototype", "In production", "Vertical slice", "Near release", "Live / shipped"];
const PLATFORMS = ["PC / Steam", "PlayStation", "Xbox", "Switch", "Mobile", "VR"];
const SCOPES = ["A single deliverable", "A small batch (2–10 items)", "A full system or feature", "A full level or chapter", "Ongoing monthly output", "Not sure — help me scope it"];
const BUDGETS = ["Under $2,500", "$2,500 – $10,000", "$10,000 – $25,000", "$25,000 – $75,000", "$75,000+", "Prefer to discuss"];
const TIMELINES = ["ASAP — urgent", "Within 1 month", "1–3 months", "3–6 months", "6+ months", "Flexible"];
const SUPPORT = ["One-off delivery", "Occasional follow-ups", "Ongoing retainer", "Not sure yet"];

const STEPS = [
  { id: "about", icon: User, title: "About You", tagline: "Who we're talking to" },
  { id: "project", icon: Gamepad2, title: "The Project", tagline: "What you're building" },
  { id: "work", icon: Wrench, title: "The Work", tagline: "Where we plug in" },
  { id: "logistics", icon: CalendarClock, title: "Logistics", tagline: "Budget & timeline" },
  { id: "review", icon: ClipboardCheck, title: "Review", tagline: "Lock it in" },
];

const INITIAL = {
  name: "", email: "", company: "", role: "", teamSize: "",
  projectName: "", projectType: "", engine: "", stage: "", platforms: [],
  disciplines: [], scope: "", references: "", description: "",
  budget: "", timeline: "", startDate: "", nda: false, ongoingSupport: "",
};

function OptionGrid({ options, value, onChange, multi = false, cols = "grid-cols-1 sm:grid-cols-2", testid }) {
  const toggle = (opt) => {
    if (multi) {
      onChange(value.includes(opt) ? value.filter((v) => v !== opt) : [...value, opt]);
    } else {
      onChange(opt);
    }
  };
  const active = (opt) => (multi ? value.includes(opt) : value === opt);
  return (
    <div className={`grid ${cols} gap-2`} data-testid={testid}>
      {options.map((opt) => (
        <button
          key={opt}
          type="button"
          data-testid={`${testid}-${opt.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
          onClick={() => toggle(opt)}
          className={`text-left font-mono text-xs px-3 py-2.5 border transition-all duration-300 flex items-center justify-between gap-2 ${
            active(opt) ? "border-nc-blue bg-nc-blue/10 text-nc-blue" : "border-white/10 text-zinc-400 hover:border-white/30 hover:text-zinc-200"
          }`}
        >
          <span>{opt}</span>
          {active(opt) && <Check className="w-3.5 h-3.5 shrink-0" />}
        </button>
      ))}
    </div>
  );
}

function Field({ label, hint, children }) {
  return (
    <div>
      <label className={labelCls}>{label}</label>
      {children}
      {hint && <p className={hintCls}>{hint}</p>}
    </div>
  );
}

export default function ContactPage() {
  const [step, setStep] = useState(0);
  const [dir, setDir] = useState(1);
  const [form, setForm] = useState(INITIAL);
  const [sending, setSending] = useState(false);
  const [reference, setReference] = useState(null);

  const set = (field) => (e) => setForm((f) => ({ ...f, [field]: e.target ? e.target.value : e }));
  const setVal = (field) => (v) => setForm((f) => ({ ...f, [field]: v }));

  const stepValid = [
    form.name.trim().length >= 2 && /.+@.+\..+/.test(form.email),
    form.projectType !== "",
    form.disciplines.length > 0 && form.description.trim().length >= 20,
    true,
    true,
  ][step];

  const go = (d) => {
    setDir(d);
    setStep((s) => Math.min(Math.max(s + d, 0), STEPS.length - 1));
  };

  const handleSubmit = async () => {
    setSending(true);
    try {
      const r = await api.post("/quotes", form);
      setReference(r.data.reference);
      toast.success("Quote brief received");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSending(false);
    }
  };

  const reviewGroups = [
    { step: 0, title: "About You", rows: [["Name / Studio", form.name], ["Email", form.email], ["Company", form.company], ["Role", form.role], ["Team size", form.teamSize]] },
    { step: 1, title: "The Project", rows: [["Project", form.projectName], ["Type", form.projectType], ["Engine", form.engine], ["Stage", form.stage], ["Platforms", form.platforms.join(", ")]] },
    { step: 2, title: "The Work", rows: [["Disciplines", form.disciplines.join(", ")], ["Scope", form.scope], ["References", form.references], ["Description", form.description]] },
    { step: 3, title: "Logistics", rows: [["Budget", form.budget], ["Timeline", form.timeline], ["Start", form.startDate], ["NDA", form.nda ? "Yes" : "No"], ["Ongoing", form.ongoingSupport]] },
  ];

  return (
    <div data-testid="contact-page" className="min-h-screen bg-[#050505] text-zinc-100 flex flex-col selection:bg-nc-blue/30">
      <Seo
        title="Get a Quote — Game Development & Modding Services"
        description="Get a scoped quote within 24 hours: Unreal Engine development, Unity, Arma Reforger, FiveM (GTA V) and Minecraft services from an Australian studio. Fixed-price milestones, NDA-covered from first contact."
        path="/contact"
        keywords="game development quote, hire game developers, unreal engine development quote, fivem developer quote, arma reforger developer hire, game outsourcing quote, Specpixel contact"
      />
      <SiteNav />

      <header className="border-b border-white/10 relative overflow-hidden">
        <div className="absolute inset-0 grid-texture opacity-50" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 py-14 md:py-20">
          <p className="font-mono text-xs tracking-[0.4em] text-nc-orange uppercase mb-4">Request a Quote // Project Brief</p>
          <h1 data-testid="contact-title" className="font-sans font-bold uppercase tracking-tight text-3xl sm:text-5xl text-zinc-50">
            Let's scope it properly.
          </h1>
          <p className="font-mono text-sm text-zinc-400 mt-4 max-w-2xl leading-relaxed">
            Five short steps. The more precisely we understand your project, the sharper the quote —
            back to you within 24 hours.
          </p>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-14 w-full grid grid-cols-1 lg:grid-cols-3 gap-10">
        <motion.aside data-testid="contact-info" className="flex flex-col gap-4" initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
          {INFO.map((item) => {
            const Icon = item.icon;
            const inner = (
              <div className="flex items-center gap-4 border border-white/10 bg-black/40 p-5 hover:border-white/30 transition-colors duration-300">
                <span className="w-10 h-10 border border-nc-blue/40 bg-nc-blue/10 flex items-center justify-center shrink-0">
                  <Icon className="w-4 h-4 text-nc-blue" />
                </span>
                <div>
                  <p className="font-mono text-[9px] tracking-[0.25em] text-zinc-500 uppercase">{item.label}</p>
                  <p className="font-mono text-sm text-zinc-100 mt-1">{item.value}</p>
                </div>
              </div>
            );
            return item.href ? (
              <a key={item.label} data-testid={`contact-${item.label.toLowerCase().replace(/[^a-z]+/g, "-")}`} href={item.href} target={item.href.startsWith("http") ? "_blank" : undefined} rel="noopener noreferrer">{inner}</a>
            ) : (
              <div key={item.label} data-testid={`contact-${item.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}>{inner}</div>
            );
          })}
          <div className="border border-nc-orange/30 bg-nc-orange/5 p-5">
            <p className="font-mono text-[9px] tracking-[0.25em] text-nc-orange uppercase mb-2">Why so many questions?</p>
            <p className="font-mono text-[11px] text-zinc-400 leading-relaxed">
              Complex projects fail on vague briefs. Every field here maps to a line item in your estimate —
              no surprises, no padded quotes.
            </p>
          </div>
        </motion.aside>

        <motion.section className="lg:col-span-2" initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.1 }}>
          {reference ? (
            <div data-testid="quote-success" className="border border-nc-green/40 bg-nc-green/5 p-10 flex flex-col items-start gap-5">
              <ShieldCheck className="w-10 h-10 text-nc-green" />
              <h2 className="font-sans font-bold uppercase text-2xl text-zinc-50">Brief locked in.</h2>
              <p className="font-mono text-xs text-zinc-400 leading-relaxed">
                Thanks {form.name} — your project brief is with our producers. Reference:
              </p>
              <p data-testid="quote-reference" className="font-mono text-lg tracking-[0.3em] text-nc-green border border-nc-green/40 bg-black/40 px-5 py-3">{reference}</p>
              <p className="font-mono text-xs text-zinc-500 leading-relaxed">
                We'll reply to {form.email} within 24 hours with a scoped estimate. Keep the reference — quote it in any follow-up.
              </p>
            </div>
          ) : (
            <div className="border border-white/10 bg-black/40">
              {/* Stepper */}
              <div className="flex border-b border-white/10 overflow-x-auto" data-testid="quote-stepper">
                {STEPS.map((s, i) => {
                  const Icon = s.icon;
                  const state = i < step ? "done" : i === step ? "active" : "todo";
                  return (
                    <button
                      key={s.id}
                      type="button"
                      data-testid={`quote-step-${s.id}`}
                      onClick={() => { if (i < step) { setDir(-1); setStep(i); } }}
                      className={`flex-1 min-w-[110px] px-4 py-4 text-left border-r border-white/10 last:border-r-0 transition-colors duration-300 ${
                        state === "active" ? "bg-nc-blue/10" : state === "done" ? "hover:bg-white/5 cursor-pointer" : "opacity-50"
                      }`}
                    >
                      <div className="flex items-center gap-2 mb-1">
                        {state === "done" ? <Check className="w-3.5 h-3.5 text-nc-green" /> : <Icon className={`w-3.5 h-3.5 ${state === "active" ? "text-nc-blue" : "text-zinc-500"}`} />}
                        <span className="font-mono text-[9px] tracking-[0.2em] text-zinc-600">{String(i + 1).padStart(2, "0")}</span>
                      </div>
                      <p className={`font-sans font-semibold uppercase text-[11px] tracking-wide ${state === "active" ? "text-nc-blue" : state === "done" ? "text-zinc-300" : "text-zinc-500"}`}>{s.title}</p>
                      <p className="font-mono text-[9px] text-zinc-600 mt-0.5 hidden sm:block">{s.tagline}</p>
                    </button>
                  );
                })}
              </div>

              <div className="p-6 md:p-8">
                <AnimatePresence mode="wait" custom={dir}>
                  <motion.div
                    key={step}
                    custom={dir}
                    initial={{ opacity: 0, x: 32 * dir }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -32 * dir }}
                    transition={{ duration: 0.35, ease: EASE }}
                  >
                    {step === 0 && (
                      <div data-testid="quote-panel-about" className="flex flex-col gap-5">
                        <Field label="Your name or studio name *" hint="So we know who to address the estimate to.">
                          <input data-testid="quote-name-input" className={inputCls} value={form.name} onChange={set("name")} placeholder="e.g. Damian / Specpixel-style Studio" />
                        </Field>
                        <Field label="Email address *" hint="Your quote and any follow-up questions land here.">
                          <input data-testid="quote-email-input" type="email" className={inputCls} value={form.email} onChange={set("email")} placeholder="you@studio.com" />
                        </Field>
                        <Field label="Company / team name" hint="If you're representing a studio or publisher.">
                          <input data-testid="quote-company-input" className={inputCls} value={form.company} onChange={set("company")} />
                        </Field>
                        <Field label="Your role" hint="Helps us pitch the quote at the right level — technical or high-level.">
                          <OptionGrid testid="quote-role" options={ROLES} value={form.role} onChange={setVal("role")} />
                        </Field>
                        <Field label="Team size" hint="Tells us how much of the pipeline you already have covered.">
                          <OptionGrid testid="quote-teamsize" options={TEAM_SIZES} value={form.teamSize} onChange={setVal("teamSize")} cols="grid-cols-2 sm:grid-cols-5" />
                        </Field>
                      </div>
                    )}

                    {step === 1 && (
                      <div data-testid="quote-panel-project" className="flex flex-col gap-5">
                        <Field label="Project name / codename" hint="Internal names are fine — we're under no illusion about NDAs.">
                          <input data-testid="quote-projectname-input" className={inputCls} value={form.projectName} onChange={set("projectName")} placeholder="e.g. Project Nightfall" />
                        </Field>
                        <Field label="What kind of engagement is this? *" hint="This shapes the entire structure of your estimate.">
                          <OptionGrid testid="quote-projecttype" options={PROJECT_TYPES} value={form.projectType} onChange={setVal("projectType")} />
                        </Field>
                        <Field label="Engine" hint="We staff UE5, Unity and CryEngine veterans — the engine decides who reads your brief first.">
                          <OptionGrid testid="quote-engine" options={ENGINES} value={form.engine} onChange={setVal("engine")} />
                        </Field>
                        <Field label="Where is the project today?" hint="A prototype needs different support than a live title.">
                          <OptionGrid testid="quote-stage" options={STAGES} value={form.stage} onChange={setVal("stage")} cols="grid-cols-2 sm:grid-cols-3" />
                        </Field>
                        <Field label="Target platforms" hint="Cert requirements and optimization budgets differ per platform. Select all that apply.">
                          <OptionGrid testid="quote-platforms" options={PLATFORMS} value={form.platforms} onChange={setVal("platforms")} multi cols="grid-cols-2 sm:grid-cols-3" />
                        </Field>
                      </div>
                    )}

                    {step === 2 && (
                      <div data-testid="quote-panel-work" className="flex flex-col gap-5">
                        <Field label="Which disciplines do you need? *" hint="Every selected discipline gets its own scoped line item in the quote. Select all that apply.">
                          <OptionGrid testid="quote-disciplines" options={SERVICES.map((s) => s.title)} value={form.disciplines} onChange={setVal("disciplines")} multi cols="grid-cols-2 sm:grid-cols-3" />
                        </Field>
                        <Field label="Rough scope" hint="Ballpark is fine — we'll refine it together on the call.">
                          <OptionGrid testid="quote-scope" options={SCOPES} value={form.scope} onChange={setVal("scope")} />
                        </Field>
                        <Field label="References / links" hint="Steam pages, ArtStation, videos, mood boards — anything that shows the bar you're aiming for.">
                          <input data-testid="quote-references-input" className={inputCls} value={form.references} onChange={set("references")} placeholder="https://…" />
                        </Field>
                        <Field label="Describe the work in your own words * (min 20 chars)" hint="What does done look like? Deliverables, quality bar, anything that keeps you up at night.">
                          <textarea data-testid="quote-description-input" rows={6} className={`${inputCls} resize-none`} value={form.description} onChange={set("description")} placeholder="e.g. We need 6 AAA police characters, MetaHuman-ready, rigged for UE5.3, delivered over 8 weeks…" />
                        </Field>
                      </div>
                    )}

                    {step === 3 && (
                      <div data-testid="quote-panel-logistics" className="flex flex-col gap-5">
                        <Field label="Budget range (USD)" hint="We quote honestly within your range — this stops us scoping a Rolls-Royce for a hatchback budget.">
                          <OptionGrid testid="quote-budget" options={BUDGETS} value={form.budget} onChange={setVal("budget")} cols="grid-cols-2 sm:grid-cols-3" />
                        </Field>
                        <Field label="When do you need it?" hint="Drives scheduling and whether we parallelise teams.">
                          <OptionGrid testid="quote-timeline" options={TIMELINES} value={form.timeline} onChange={setVal("timeline")} cols="grid-cols-2 sm:grid-cols-3" />
                        </Field>
                        <Field label="Ideal start date" hint="Optional — helps us hold capacity.">
                          <input data-testid="quote-startdate-input" type="date" className={inputCls} value={form.startDate} onChange={set("startDate")} />
                        </Field>
                        <Field label="Ongoing support after delivery?" hint="Many clients keep us on retainer after the first drop.">
                          <OptionGrid testid="quote-support" options={SUPPORT} value={form.ongoingSupport} onChange={setVal("ongoingSupport")} cols="grid-cols-2 sm:grid-cols-4" />
                        </Field>
                        <button
                          type="button"
                          data-testid="quote-nda-toggle"
                          onClick={() => setForm((f) => ({ ...f, nda: !f.nda }))}
                          className={`flex items-center gap-3 border px-4 py-3 transition-colors duration-300 ${form.nda ? "border-nc-orange bg-nc-orange/10" : "border-white/10 hover:border-white/30"}`}
                        >
                          <span className={`w-5 h-5 border flex items-center justify-center ${form.nda ? "border-nc-orange bg-nc-orange" : "border-white/30"}`}>
                            {form.nda && <Check className="w-3.5 h-3.5 text-white" />}
                          </span>
                          <span className="font-mono text-xs text-zinc-300 text-left">
                            NDA required before sharing details
                            <span className="block text-[10px] text-zinc-600 mt-0.5">We'll sign yours, or issue ours — standard practice either way.</span>
                          </span>
                        </button>
                      </div>
                    )}

                    {step === 4 && (
                      <div data-testid="quote-panel-review" className="flex flex-col gap-5">
                        <p className="font-mono text-xs text-zinc-400 leading-relaxed">
                          One last look. Jump back to any section to adjust — then lock it in.
                        </p>
                        {reviewGroups.map((g) => (
                          <div key={g.title} className="border border-white/10">
                            <div className="flex items-center justify-between px-4 py-2.5 bg-white/5 border-b border-white/10">
                              <span className="font-mono text-[10px] tracking-[0.25em] text-nc-blue uppercase">{g.title}</span>
                              <button type="button" data-testid={`quote-edit-${g.title.toLowerCase().replace(/[^a-z]+/g, "-")}`} onClick={() => { setDir(-1); setStep(g.step); }} className="font-mono text-[10px] tracking-[0.2em] text-zinc-500 hover:text-nc-orange uppercase transition-colors duration-300">
                                Edit
                              </button>
                            </div>
                            <div className="p-4 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2.5">
                              {g.rows.filter(([, v]) => v && v.length).map(([k, v]) => (
                                <div key={k}>
                                  <p className="font-mono text-[9px] tracking-[0.2em] text-zinc-600 uppercase">{k}</p>
                                  <p className="font-mono text-xs text-zinc-200 mt-0.5 break-words whitespace-pre-wrap">{v}</p>
                                </div>
                              ))}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </motion.div>
                </AnimatePresence>
              </div>

              {/* Nav */}
              <div className="flex items-center justify-between border-t border-white/10 px-6 md:px-8 py-4">
                <button
                  type="button"
                  data-testid="quote-back-button"
                  onClick={() => go(-1)}
                  disabled={step === 0}
                  className="font-mono text-[10px] tracking-[0.25em] uppercase flex items-center gap-2 text-zinc-400 hover:text-zinc-100 disabled:opacity-30 transition-colors duration-300"
                >
                  <ChevronLeft className="w-4 h-4" /> Back
                </button>
                <span data-testid="quote-progress" className="font-mono text-[10px] tracking-[0.3em] text-zinc-600 uppercase">
                  Step {step + 1} / {STEPS.length}
                </span>
                {step < STEPS.length - 1 ? (
                  <button
                    type="button"
                    data-testid="quote-next-button"
                    onClick={() => go(1)}
                    disabled={!stepValid}
                    className="bg-nc-blue hover:bg-nc-blue/80 disabled:opacity-30 text-white font-sans font-semibold uppercase tracking-[0.2em] text-xs px-6 py-3 flex items-center gap-2 transition-colors duration-300"
                  >
                    Next <ChevronRight className="w-4 h-4" />
                  </button>
                ) : (
                  <button
                    type="button"
                    data-testid="quote-submit-button"
                    onClick={handleSubmit}
                    disabled={sending || !form.name || !form.email || form.disciplines.length === 0 || form.description.trim().length < 20 || !form.projectType}
                    className="bg-nc-orange hover:bg-nc-orange/80 disabled:opacity-30 text-white font-sans font-semibold uppercase tracking-[0.2em] text-xs px-6 py-3 flex items-center gap-2 transition-colors duration-300"
                    style={{ boxShadow: "0 0 24px rgba(255,107,26,0.3)" }}
                  >
                    <Send className="w-4 h-4" /> {sending ? "Sending..." : "Submit Brief"}
                  </button>
                )}
              </div>
            </div>
          )}

          {!reference && (
            <p className="font-mono text-[10px] text-zinc-600 mt-4 flex items-center gap-2">
              <FileText className="w-3.5 h-3.5" />
              Prefer email? Write to us directly at business@specpixel.com — a human replies either way.
            </p>
          )}
        </motion.section>
      </main>

      <SiteFooter />
    </div>
  );
}
